import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HAMMER_LOADER, HammerModule } from '@angular/platform-browser';
import {
  ApplicationConfig,
  ErrorHandler,
  HostListener,
  importProvidersFrom,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { CustomTranslateHttpLoader, OscReferenceAppCommonModule } from 'osc-reference-app-common';
import { environment } from 'src/environments/environment';
import { platform } from 'src/platforms/platform';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkTreeModule } from '@angular/cdk/tree';
import { DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule, MAT_BOTTOM_SHEET_DEFAULT_OPTIONS } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CoreModule } from '@core/core.module';
import { getEnvironment } from '@core/helpers/environment.helper';
import { IpmInterceptor } from '@core/interceptor/ipm-interceptor';
import { AppErrorHandler } from '@core/services/app-error-handler.service';
import { BluetoothService } from '@core/services/bluetooth.service';
import { MeasurementDeviceService } from '@core/services/measurement-device.service';
import { MeasurementUnitTypeService } from '@core/services/measurement-unit-type-service';
import { BluetoothMockService } from '@core/services/mock/bluetooth-mock.service';
import { MeasurementDeviceMockService } from '@core/services/mock/measurement-device-mock.service';
import { MeasurementUnitTypeMockService } from '@core/services/mock/measurement-unit-type-mock-service';
import { OfflineDataMockService } from '@core/services/mock/offline-data-mock-service';
import { OfflineDataService } from '@core/services/offline-data.service';
import { dataActionReducer } from '@core/store/reducers/data.reducer';
import { featureToggleActionReducer } from '@core/store/reducers/feature-toggle.reducer';
import { filesActionReducer } from '@core/store/reducers/files.reducer';
import { inspectionRequestsActionReducer } from '@core/store/reducers/inspection-request.reducer';
import { myItemsActionReducer } from '@core/store/reducers/my-items.reducer';
import { uiActionReducer } from '@core/store/reducers/ui.reducer';
import { IonicModule, Platform } from '@ionic/angular';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule, TranslateLoader, TranslateService, TranslateCompiler } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { SharedModule } from '@shared/shared.module';
import { ApplicationAdminGuard } from '@osc-utils/authentication/application-admin-guard';
import { OscPlatform } from '@osc-utils/enums/osc-platform';
import { OscUtilsModule } from '@osc-utils/osc-utils.module';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { getAuthProviders, PlatformService, provideEnv } from '@evc/platform';
import { ThemeService } from '@evc/web-components';
import { NetworkStatusService } from '@core/services/network-status.service';
import { ForbiddenInterceptor } from '@core/interceptor/forbidden.interceptor';
import { EvcAuthService } from '@core/services/evc-auth.service';
import { firstValueFrom } from 'rxjs';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { DeviceService } from '@core/services/device.service';
import { AuthInterceptor } from '@core/interceptor/auth.interceptor';
import { StatusBar } from '@capacitor/status-bar';
import { GestureConfig } from './config/gesture-config';
import { DeficiencyModule } from './deficiency/deficiency.module';
import { AppRoutingModule } from './app.routing';
import { RouterConfig } from './mobile/const/router-config';

export const httpLoaderFactory = (http: HttpClient): CustomTranslateHttpLoader => {
  const resources = [{ prefix: 'assets/i18n/', suffix: '.json' }];
  return CustomTranslateHttpLoader.CustomHttpLoaderFactory(http, resources);
};

export const getPlatform = (): OscPlatform => platform.name as OscPlatform;

export const initConfig =
  (
    configService: ConfigurationService,
    ionicPlatform: Platform,
    themeService: ThemeService,
    networkStatusService: NetworkStatusService,
    translate: TranslateService,
    evcAuthService: EvcAuthService,
    platformService: PlatformService,
    deviceService: DeviceService,
  ) =>
  () => {
    // HACK: Since we are not using evc-platform on mobile, we need to specify a theme.
    // Otherwise, it will use the one defined on the system by default and we don't support dark mode.
    themeService.setTheme('light');
    translate.setFallbackLang('en');
    const initPromises: Promise<any>[] = [
      configService.load(),
      ionicPlatform.ready().then(() =>
        Promise.all([
          networkStatusService.init().then(() => {
            // Initialize the services early since some of them depends on auth service to be initialized before the app renders.
            // IMPORTANT: We don't use `await` since it can block the app from loading properly.
            evcAuthService.init().then(() => platformService.init());
          }),
          initAppLinks(),
          deviceService.init(),
        ]),
      ),
      firstValueFrom(translate.use('en')),
    ];
    return Promise.all(initPromises);
  };

declare let cordova: any;
export const appConfig: ApplicationConfig = {
  providers: [
    provideEnv(environment),
    importProvidersFrom(
      StoreModule.forRoot(
        {
          data: dataActionReducer,
          myItems: myItemsActionReducer,
          files: filesActionReducer,
          inspectionRequests: inspectionRequestsActionReducer,
          ui: uiActionReducer,
          codes: featureToggleActionReducer,
        },
        {
          runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
            strictStateSerializability: false,
            strictActionSerializability: false,
          },
        },
      ),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        extend: true,
        isolate: false,
      }),
      AppRoutingModule,
      BrowserModule,
      CdkTreeModule,
      DragDropModule,
      DeficiencyModule,
      FormsModule,
      LayoutModule,
      MatAutocompleteModule,
      MatBadgeModule,
      MatBottomSheetModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatCheckboxModule,
      MatChipsModule,
      MatDatepickerModule,
      MatDialogModule,
      MatDividerModule,
      MatExpansionModule,
      MatGridListModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatMenuModule,
      MatNativeDateModule,
      MatPaginatorModule,
      MatProgressBarModule,
      MatProgressSpinnerModule,
      MatSelectModule,
      MatSidenavModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatSortModule,
      MatStepperModule,
      MatTableModule,
      MatTabsModule,
      MatToolbarModule,
      MatTooltipModule,
      MatTreeModule,
      MatRadioModule,
      OscReferenceAppCommonModule,
      OscUtilsModule.forRoot(),
      ReactiveFormsModule,
      ScrollingModule,
      platform.name === OscPlatform.Android ? IonicModule.forRoot({ mode: 'md' }) : [],
      platform.name === OscPlatform.IOs ? IonicModule.forRoot({ mode: 'ios' }) : [],
      SharedModule,
      CoreModule,
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    ),
    ApplicationAdminGuard,
    DatePipe,
    HttpClient,
    { provide: 'OSC_ENVIRONMENT', useFactory: getEnvironment },
    { provide: 'PLATFORM', useFactory: getPlatform },
    { provide: MAT_BOTTOM_SHEET_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    provideAppInitializer(() =>
      initConfig(
        inject(ConfigurationService),
        inject(Platform),
        inject(ThemeService),
        inject(NetworkStatusService),
        inject(TranslateService),
        inject(EvcAuthService),
        inject(PlatformService),
        inject(DeviceService),
      )(),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: IpmInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ForbiddenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    HostListener,
    {
      provide: 'MeasurementDeviceService',
      useClass: environment.name === 'Simulator' ? MeasurementDeviceMockService : MeasurementDeviceService,
    },
    {
      provide: 'BluetoothService',
      useClass: environment.name === 'Simulator' ? BluetoothMockService : BluetoothService,
    },
    {
      provide: 'OfflineDataService',
      useClass: environment.name === 'Simulator' ? OfflineDataMockService : OfflineDataService,
    },
    {
      provide: 'MeasurementUnitTypeService',
      useClass: environment.name === 'Simulator' ? MeasurementUnitTypeMockService : MeasurementUnitTypeService,
    },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: HAMMER_LOADER,
      useValue: () => import('hammerjs'),
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: GestureConfig,
    },
    // IMPORTANT: This module needs to be imported after HAMMER_GESTURE_CONFIG. Otherwise, gestures won't work.
    importProvidersFrom(HammerModule),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    ...getAuthProviders(environment, {
      redirects: {
        success: environment.EVC_REDIRECT_URL,
        logout: platform.name === OscPlatform.Web ? undefined : environment.EVC_REDIRECT_URL,
      },
      onRedirectNavigate(url) {
        if (platform.name === OscPlatform.Web) {
          return true;
        }
        fetchAuthenticationCode(url).then(slug => window.location.replace(`${window.location.origin}${RouterConfig.auth}/#${slug}`));
        return false;
      },
    }),
  ],
};

function fetchAuthenticationCode(url: string) {
  // We wait for the inappbrowser to give us the auth slug to apply it inside the main web view.
  return new Promise<string>((res, rej) => {
    try {
      // To fix https://evident-products.atlassian.net/browse/IPM-2596
      StatusBar.setOverlaysWebView({ overlay: false });
      const browser = cordova.InAppBrowser.open(url, '_blank', 'location=no,toolbar=no,clearcache=no,clearsessioncache=no,cleardata=no');
      browser.addEventListener('loadstart', (e: { url: string }) => {
        const slug = e.url
          .split('/#')
          .filter((_, i) => i > 0)
          .join('/#');
        if (!slug) {
          return;
        }
        browser.close();
        res(slug);
      });
    } catch (e) {
      rej(e);
    }
  });
}

// Deep Linking configuration
function initAppLinks() {
  if (platform.name === OscPlatform.Web) {
    return;
  }
  return App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    // Can't use Zone or Router here since the app is "blocked" waiting for the login.
    // So we need to use window.location.replace to navigate and force the MSAL system
    // to handle the auth and then we redirect back to the app.
    const slug = event.url
      .split('.com/#')
      .filter((_, i) => i > 0)
      .join('.com/#');
    if (!slug) {
      return;
    }

    // We need to redirect to a custom, not protected route to injest the MSAL code.
    // This is because the regular routes are protected by the AuthGuard and the MSAL integration form platform.
    // This is an issue because if we don't let MSAL inject the code before the auth guard, we will get
    // into an infinite loop of redirects to login.
    window.location.replace(`${window.location.origin}${RouterConfig.auth}/#${slug}`);
  });
}
